import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  ModelPurposeAccordion,
  type PurposeLabels,
} from "@/shared/components/ai/ModelPurposeAccordion";
import {
  aprovado,
  nivelVazio,
  PROFUNDIDADE_MAXIMA_CONTINGENCIA,
  type FieldCheck,
  type NivelDaCadeia,
  type PurposeChoice,
} from "@/shared/types/aiModels";
import { type ToolOption } from "@/shared/components/ui/tool-select";
import { isModelless, type ModelPurpose } from "@/shared/utils/modelCatalog";

/**
 * A CADEIA de contingência: uma lista ordenada de alternativas (AB#85617).
 *
 * O dado e o gateway sempre suportaram N elos — `priority` descreve uma cadeia, e a
 * montagem de headers percorre quantos alvos receber. O que limitava a um era a leitura
 * pedir a prioridade 1 pelo nome, e a tela oferecer um bloco só.
 *
 * === Por que a cadeia é apresentada por NÍVEL, e não por finalidade ===
 *
 * No dado, cada finalidade tem a sua cadeia, de comprimento independente. Na tela isso
 * viraria N listas paralelas, e quem configura teria de manter a ordem de cada uma na
 * cabeça. Por nível — "primeira alternativa", "segunda" — a leitura é uma só: se tudo
 * falhar, tenta este, depois aquele. Finalidade deixada em branco num nível simplesmente
 * não entra na cadeia dela, e é assim que os comprimentos voltam a ser independentes na
 * gravação.
 *
 * === Por que cada elo cuida dos próprios efeitos ===
 *
 * O número de elos é dinâmico, e `useEffect` não pode ser chamado em laço. Cada elo é um
 * componente, com o próprio catálogo e a própria sonda; o pai recebe de volta apenas se
 * aquele elo está pendente, que é o que decide o salvar do conjunto.
 */

export type FallbackChainTexts = {
  /** Título de um elo, recebendo a posição dele: "Alternativa 1". */
  linkTitle: (posicao: number) => string;
  addLink: string;
  removeLink: string;
  /** Explicação de por que existe mais de um nível. */
  hint: string;
  empty: string;
};

type LinkProps<P extends ModelPurpose> = {
  purposes: readonly P[];
  labels: Record<P, PurposeLabels>;
  values: Record<P, PurposeChoice>;
  providers: ToolOption[];
  providersByPurpose?: Partial<Record<P, ToolOption[]>>;
  loadingProviders?: boolean;
  notConfiguredLabel: string;
  selectModelPlaceholder: string;
  onChange: (purpose: P, patch: Partial<PurposeChoice>) => void;
  /** Os modelos que aquela integração declara. */
  loadCatalog: (purpose: P, integrationId: string) => Promise<string[]>;
  /** O veredito do provedor para o par escolhido. */
  testModel: (purpose: P, integrationId: string, model: string) => Promise<FieldCheck>;
  onPendingChange: (pendente: boolean) => void;
};

function FallbackChainLink<P extends ModelPurpose>({
  purposes,
  labels,
  values,
  providers,
  providersByPurpose,
  loadingProviders,
  notConfiguredLabel,
  selectModelPlaceholder,
  onChange,
  loadCatalog,
  testModel,
  onPendingChange,
}: LinkProps<P>) {
  // `Object.fromEntries` devolve `{[k: string]: T}` e perde a chave literal; o reduce
  // preserva `P` e mantém o acesso por finalidade checado.
  const vazio = () =>
    purposes.reduce((acc, p) => ({ ...acc, [p]: [] }), {} as Record<P, string[]>);
  const ocioso = () =>
    purposes.reduce(
      (acc, p) => ({ ...acc, [p]: { state: "idle" } as FieldCheck }),
      {} as Record<P, FieldCheck>
    );

  /**
   * As funções de fora ficam em ref, e não nas dependências dos efeitos.
   *
   * Quem usa a cadeia passa `loadCatalog`/`testModel`/`onChange` como literais — identidade
   * nova a cada render. Nas dependências, o efeito abaixo rodaria em toda renderização,
   * escreveria estado, e renderizaria de novo: um laço infinito batendo no provedor. O que
   * deve disparar a carga é o PAR escolhido mudar, e é só ele que fica na assinatura.
   */
  const loadCatalogRef = useRef(loadCatalog);
  const testModelRef = useRef(testModel);
  const onChangeRef = useRef(onChange);
  const onPendingChangeRef = useRef(onPendingChange);
  loadCatalogRef.current = loadCatalog;
  testModelRef.current = testModel;
  onChangeRef.current = onChange;
  onPendingChangeRef.current = onPendingChange;

  const [catalogos, setCatalogos] = useState<Record<P, string[]>>(vazio);
  const [checks, setChecks] = useState<Record<P, FieldCheck>>(ocioso);
  const [aberto, setAberto] = useState<string[]>([]);

  /** O par escolhido de UMA finalidade. É a mudança dele que manda recarregar e sondar. */
  const assinaturaDe = (purpose: P) =>
    `${values[purpose].integrationId ?? ""}:${values[purpose].model}`;

  const assinatura = purposes.map((p) => `${p}=${assinaturaDe(p)}`).join("|");

  /**
   * A última assinatura JÁ atendida de cada finalidade, e como cancelar a carga em voo dela.
   *
   * O efeito é um só — o número de finalidades é do contrato, e `useEffect` não pode ser
   * chamado em laço —, mas ele não pode tratar as finalidades como um bloco: trocar o
   * provedor da transcrição recolocava conversa e indexação em `checking` e sondava o
   * provedor de novo pelas três, a cada tecla. Comparando finalidade a finalidade, o efeito
   * mexe apenas na que mudou. É o mesmo critério de `useFinalidade`, no bloco principal.
   */
  const atendidas = useRef<Partial<Record<P, string>>>({});
  const cancelamentos = useRef<Partial<Record<P, () => void>>>({});

  const carregar = useCallback(
    async (purpose: P, integrationId: string | null, model: string, cancelado: () => boolean) => {
      if (!integrationId) {
        if (!cancelado()) {
          setCatalogos((atual) => ({ ...atual, [purpose]: [] }));
          setChecks((atual) => ({ ...atual, [purpose]: { state: "idle" } }));
        }
        return;
      }

      const declarados = await loadCatalogRef.current(purpose, integrationId);
      if (cancelado()) return;
      setCatalogos((atual) => ({ ...atual, [purpose]: declarados }));

      // Provedor sem modelo (AB#86885): o marcador é a única escolha, e o campo nem aparece.
      // A troca refaz a assinatura, e a sonda roda na volta.
      if (isModelless(declarados) && model !== declarados[0]) {
        onChangeRef.current(purpose, { model: declarados[0] });
        return;
      }

      // Modelo que a nova integração não declara não pode continuar selecionado: ele sairia
      // com a credencial dela e falharia no provedor.
      if (model && !declarados.includes(model)) {
        onChangeRef.current(purpose, { model: "" });
        return;
      }

      if (!model) {
        setChecks((atual) => ({ ...atual, [purpose]: { state: "idle" } }));
        return;
      }

      setChecks((atual) => ({ ...atual, [purpose]: { state: "checking" } }));
      const veredito = await testModelRef.current(purpose, integrationId, model);
      if (cancelado()) return;
      setChecks((atual) => ({ ...atual, [purpose]: veredito }));
    },
    // As funções de fora entram por ref, pelo motivo documentado acima.
    []
  );

  useEffect(() => {
    for (const purpose of purposes) {
      const atual = `${values[purpose].integrationId ?? ""}:${values[purpose].model}`;
      if (atendidas.current[purpose] === atual) continue;
      atendidas.current[purpose] = atual;

      // Só a carga DESTA finalidade é abortada: trocar de provedor dispara uma busca nova
      // sem cancelar a anterior, e a que chegasse por último venceria — a tela mostraria os
      // modelos da integração errada.
      cancelamentos.current[purpose]?.();
      let cancelado = false;
      cancelamentos.current[purpose] = () => {
        cancelado = true;
      };
      void carregar(purpose, values[purpose].integrationId, values[purpose].model, () => cancelado);
    }
    // `values` é lido pela assinatura, que é o que muda quando uma escolha muda; o objeto
    // ganha identidade nova a cada render e reiniciaria tudo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, purposes, carregar]);

  // Elo removido no meio da cadeia não pode deixar sonda em voo escrevendo no estado dele.
  useEffect(() => {
    const emVoo = cancelamentos.current;
    return () => {
      for (const cancelar of Object.values(emVoo)) (cancelar as (() => void) | undefined)?.();
      // E o que foi cancelado NÃO foi atendido: sem esquecer a assinatura, uma remontagem
      // (é o que o StrictMode faz em desenvolvimento) encontraria tudo marcado como já
      // tratado e abriria a tela sem sondar nada — a cadeia salva aparecia sem veredito.
      atendidas.current = {};
    };
  }, []);

  // Finalidade PREENCHIDA que não passou no teste é o que bloqueia. Deixar em branco
  // continua valendo: é assim que um nível cobre só a conversa, por exemplo.
  const pendente = purposes.some((purpose) => {
    const { integrationId, model } = values[purpose];
    if (!integrationId || !model) return false;
    return !aprovado(checks[purpose]);
  });

  // Só quando o veredito do elo VIRA: avisar a cada render escreveria no pai sem mudança
  // nenhuma, e o pai renderiza este elo de volta.
  useEffect(() => {
    onPendingChangeRef.current(pendente);
  }, [pendente]);

  return (
    <ModelPurposeAccordion
      purposes={purposes}
      labels={labels}
      values={values}
      checks={checks}
      catalogs={catalogos}
      providers={providers}
      providersByPurpose={providersByPurpose}
      loadingProviders={loadingProviders}
      open={aberto}
      onOpenChange={setAberto}
      onChange={onChange}
      notConfiguredLabel={notConfiguredLabel}
      selectModelPlaceholder={selectModelPlaceholder}
    />
  );
}

type ChainProps<P extends ModelPurpose> = Omit<
  LinkProps<P>,
  "values" | "onChange" | "onPendingChange"
> & {
  /** Os níveis, na ordem de tentativa. O índice 0 entra depois do principal. */
  niveis: Array<NivelDaCadeia<P>>;
  onNiveisChange: (niveis: Array<NivelDaCadeia<P>>) => void;
  onPendingChange: (pendente: boolean) => void;
  texts: FallbackChainTexts;
};

export function FallbackChain<P extends ModelPurpose>({
  niveis,
  onNiveisChange,
  onPendingChange,
  texts,
  purposes,
  ...resto
}: ChainProps<P>) {
  const { t } = useTranslation();

  // Pendência por ID e não por posição, pelo mesmo motivo da `key`: por posição, remover o
  // nível do meio deixava a pendência do removido valendo para quem tomou o lugar dele.
  const [pendencias, setPendencias] = useState<Record<string, boolean>>({});

  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;

  // O conjunto está pendente se QUALQUER elo está. Um elo mal configurado no meio da cadeia
  // é tão inútil quanto um no começo: a tentativa acontece, falha, e custa latência.
  //
  // Percorre os níveis ATUAIS: pendência de nível já removido fica para trás no mapa e não
  // conta, o que dispensa limpá-lo quando alguém remove um nível.
  useEffect(() => {
    onPendingChangeRef.current(niveis.some((nivel) => pendencias[nivel.id]));
  }, [pendencias, niveis]);

  const marcarPendencia = useCallback((id: string, pendente: boolean) => {
    setPendencias((atual) => (atual[id] === pendente ? atual : { ...atual, [id]: pendente }));
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{texts.hint}</p>

      {niveis.length === 0 ? (
        <p className="text-sm text-muted-foreground">{texts.empty}</p>
      ) : (
        niveis.map((nivel, indice) => (
          <div key={nivel.id} className="rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-sm font-medium">{texts.linkTitle(indice + 1)}</h4>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground hover:text-destructive"
                onClick={() => onNiveisChange(niveis.filter((n) => n.id !== nivel.id))}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {texts.removeLink}
              </Button>
            </div>

            <FallbackChainLink
              {...resto}
              purposes={purposes}
              values={nivel.escolhas}
              onChange={(purpose, patch) =>
                onNiveisChange(
                  niveis.map((n) =>
                    n.id === nivel.id
                      ? {
                          ...n,
                          escolhas: {
                            ...n.escolhas,
                            [purpose]: { ...n.escolhas[purpose], ...patch },
                          },
                        }
                      : n
                  )
                )
              }
              onPendingChange={(pendente) => marcarPendencia(nivel.id, pendente)}
            />
          </div>
        ))
      )}

      {niveis.length < PROFUNDIDADE_MAXIMA_CONTINGENCIA && (
        <Button
          type="button"
          variant="outline"
          className="gap-1"
          onClick={() => onNiveisChange([...niveis, nivelVazio(purposes)])}
        >
          <Plus className="h-4 w-4" />
          {texts.addLink}
        </Button>
      )}

      {niveis.length >= PROFUNDIDADE_MAXIMA_CONTINGENCIA && (
        <p className="text-xs text-muted-foreground">
          {t("settings.fallbackChain.maxReached", {
            limit: PROFUNDIDADE_MAXIMA_CONTINGENCIA,
          })}
        </p>
      )}
    </div>
  );
}
